// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { useEffect, useState } from 'react';
import Loading from '../components/Loading';
import { useConfig } from '../hooks/other/useConfig';
import { useOperatorParty } from '../hooks/queries/utility-operator/useOperatorParty';
import LedgerApiClientV2 from '../utils/LedgerApiClientV2';
import { useJsonApiClient, useWsSupport } from './JsonApiContext';
import { LedgerApiContextV2 } from './LedgerApiContext';
import { useModuleParty } from './ModuleContext';
import { useUserState } from './UserContext';

export const LedgerApiClientProvider = ({ children }: React.PropsWithChildren) => {
  const config = useConfig();
  const operator = useOperatorParty();
  const party = useModuleParty();
  const { loggedInUser } = useUserState();
  const jsonApi = useJsonApiClient();
  // Websockets supported Ledger API v2 / ACS
  // do not pass wsSupport  to LedgerApiClientV2 in order to disable websockets
  const wsSupport = useWsSupport();

  const [ledgerApiClientV2, setLedgerApiClientV2] = useState<LedgerApiClientV2 | undefined>(
    undefined,
  );
  useEffect(() => {
    const loadLedgerApiClientV2 = async () => {
      if (loggedInUser && operator.data) {
        // client for JSON API v2
        const apiClientV2 = new LedgerApiClientV2(
          jsonApi,
          loggedInUser.userId,
          party,
          config.app.deduplication_duration_in_seconds,
          wsSupport,
        );
        await apiClientV2.initialize(operator.data);
        setLedgerApiClientV2(apiClientV2);
      }
    };
    loadLedgerApiClientV2().catch((error) =>
      console.error(`Failed to load ledgerApiClientV2: ${error}`),
    );
  }, [jsonApi, wsSupport, loggedInUser, party, config, operator.data]);

  if (!ledgerApiClientV2) {
    return <Loading text="Initializing ledger client" />;
  }

  return (
    <LedgerApiContextV2.Provider value={ledgerApiClientV2}>{children}</LedgerApiContextV2.Provider>
  );
};
